import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  TextField,
  Grid,
  Typography,
  InputAdornment,
  Alert,
} from "@mui/material";
import { TargetData } from "../types";

interface EditFormProps {
  selectedTarget: TargetData;
  selectedNight: string;
  onSave: (data: TargetData) => void;
  prepareDataRef?: React.MutableRefObject<() => TargetData>;
}

// Move ALL static objects outside component
const sectionHeaderSx = {
  mb: 3,
  color: "primary.main",
  fontWeight: 600,
  fontSize: 18,
  textTransform: "uppercase",
  letterSpacing: "1px",
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-8px",
    left: 0,
    width: "40px",
    height: "2px",
    backgroundColor: "primary.main",
  },
};

const gridSpacing = { spacing: 3, sx: { mb: 4 } };
const gridSpacingLast = { spacing: 3 };
const textFieldProps = {
  variant: "outlined" as const,
  margin: "normal" as const,
  fullWidth: true,
};

const mmAdornment = <InputAdornment position="end">mm</InputAdornment>;
const bortleAdornment = <InputAdornment position="end">Bortle</InputAdornment>;
const meterAdornment = <InputAdornment position="end">m</InputAdornment>;

// Memoized text field component to prevent re-renders
const MemoizedTextField = React.memo(({ onChange, ...props }: any) => (
  <TextField {...textFieldProps} {...props} onChange={onChange} />
));

const EditForm: React.FC<EditFormProps> = ({
  selectedTarget,
  selectedNight,
  onSave,
  prepareDataRef,
}) => {
  // Define form data type
  type FormData = {
    name: string;
    filters: string[];
    raCoordinates: string;
    decCoordinates: string;
    telescopeName: string;
    cameraName: string;
    focalLength: string;
    focalRatio: string;
    pixelSize: string;
    bitDepth: string;
    locationName: string;
    bortle: string;
    elevation: string;
  };

  // Use refs for immediate values to avoid re-renders
  const formDataRef = useRef<FormData>({
    name: selectedTarget.name,
    filters: selectedTarget.filters,
    raCoordinates: "",
    decCoordinates: "",
    telescopeName: selectedTarget.equipment.telescopeName,
    cameraName: selectedTarget.equipment.cameraName,
    focalLength: selectedTarget.equipment.focalLength.toString(),
    focalRatio: selectedTarget.equipment.focalRatio.toString(),
    pixelSize: selectedTarget.equipment.pixelSize.toString(),
    bitDepth: selectedTarget.equipment.bitDepth.toString(),
    locationName: "",
    bortle: "",
    elevation: "",
  });

  // Only keep display states for UI
  const [displayValues, setDisplayValues] = useState(() => {
    const isNightSelected = selectedNight !== "total";
    const nightData = isNightSelected
      ? selectedTarget.nightMetadata[selectedNight]
      : null;

    return {
      name: selectedTarget.name,
      filters: selectedTarget.filters.join(", "),
      raCoordinates: nightData?.acquisitionDetails.RACoordinates || "",
      decCoordinates: nightData?.acquisitionDetails.DECCoordinates || "",
      telescopeName: selectedTarget.equipment.telescopeName,
      cameraName: selectedTarget.equipment.cameraName,
      focalLength: selectedTarget.equipment.focalLength.toString(),
      focalRatio: selectedTarget.equipment.focalRatio.toString(),
      pixelSize: selectedTarget.equipment.pixelSize.toString(),
      bitDepth: selectedTarget.equipment.bitDepth.toString(),
      locationName: nightData?.location.name || "",
      bortle: nightData?.location.bortle?.toString() || "",
      elevation: nightData?.location.elevation?.toString() || "",
    };
  });

  const isNightSelected = selectedNight !== "total";

  // Update display values when selectedNight changes (only once)
  useEffect(() => {
    const nightData = isNightSelected
      ? selectedTarget.nightMetadata[selectedNight]
      : null;
    const newValues = {
      name: selectedTarget.name,
      filters: selectedTarget.filters.join(", "),
      raCoordinates: nightData?.acquisitionDetails.RACoordinates || "",
      decCoordinates: nightData?.acquisitionDetails.DECCoordinates || "",
      telescopeName: selectedTarget.equipment.telescopeName,
      cameraName: selectedTarget.equipment.cameraName,
      focalLength: selectedTarget.equipment.focalLength.toString(),
      focalRatio: selectedTarget.equipment.focalRatio.toString(),
      pixelSize: selectedTarget.equipment.pixelSize.toString(),
      bitDepth: selectedTarget.equipment.bitDepth.toString(),
      locationName: nightData?.location.name || "",
      bortle: nightData?.location.bortle?.toString() || "",
      elevation: nightData?.location.elevation?.toString() || "",
    };

    setDisplayValues(newValues);
    formDataRef.current = {
      ...formDataRef.current,
      ...newValues,
      filters: selectedTarget.filters, // Keep as array in ref
    };
  }, [selectedNight, selectedTarget, isNightSelected]);

  // Super lightweight change handlers that only update display
  const createChangeHandler = useCallback((field: keyof FormData) => {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      // Update display immediately
      setDisplayValues((prev) => ({ ...prev, [field]: value }));

      // Update ref for data preparation with proper typing
      if (field === "filters") {
        (formDataRef.current[field] as string[]) = value
          .split(",")
          .map((f: string) => f.trim());
      } else {
        (formDataRef.current[field] as string) = value;
      }
    };
  }, []);

  // Create handlers once
  const handlers = useMemo(
    () => ({
      name: createChangeHandler("name"),
      filters: createChangeHandler("filters"),
      raCoordinates: createChangeHandler("raCoordinates"),
      decCoordinates: createChangeHandler("decCoordinates"),
      telescopeName: createChangeHandler("telescopeName"),
      cameraName: createChangeHandler("cameraName"),
      focalLength: createChangeHandler("focalLength"),
      focalRatio: createChangeHandler("focalRatio"),
      pixelSize: createChangeHandler("pixelSize"),
      bitDepth: createChangeHandler("bitDepth"),
      locationName: createChangeHandler("locationName"),
      bortle: createChangeHandler("bortle"),
      elevation: createChangeHandler("elevation"),
    }),
    [createChangeHandler]
  );

  // Expose prepareData method for parent to call when needed
  const prepareData = useCallback((): TargetData => {
    if (!selectedTarget) {
      throw new Error("No target selected");
    }

    const data = {
      ...selectedTarget,
      name: formDataRef.current.name,
      filters: formDataRef.current.filters,
      nightMetadata: { ...selectedTarget.nightMetadata },
      equipment: {
        telescopeName: formDataRef.current.telescopeName,
        cameraName: formDataRef.current.cameraName,
        focalLength: parseFloat(formDataRef.current.focalLength) || 0,
        focalRatio: parseFloat(formDataRef.current.focalRatio) || 0,
        pixelSize: parseFloat(formDataRef.current.pixelSize) || 0,
        bitDepth: parseInt(formDataRef.current.bitDepth) || 0,
      },
    };

    if (isNightSelected) {
      const existingNightData =
        selectedTarget.nightMetadata[selectedNight] || {};
      data.nightMetadata[selectedNight] = {
        ...existingNightData,
        acquisitionDetails: {
          ...existingNightData.acquisitionDetails,
          RACoordinates: formDataRef.current.raCoordinates,
          DECCoordinates: formDataRef.current.decCoordinates,
          TelescopeName: formDataRef.current.telescopeName,
          CameraName: formDataRef.current.cameraName,
          FocalLength: parseFloat(formDataRef.current.focalLength) || 0,
          FocalRatio: parseFloat(formDataRef.current.focalRatio) || 0,
        },
        location: {
          ...existingNightData.location,
          name: formDataRef.current.locationName,
          bortle: parseInt(formDataRef.current.bortle) || 0,
          elevation: parseInt(formDataRef.current.elevation) || 0,
        },
      };
    }

    return data;
  }, [selectedTarget, selectedNight, isNightSelected]);

  // Update prepareDataRef when it changes
  useEffect(() => {
    if (prepareDataRef) {
      prepareDataRef.current = prepareData;
    }
  }, [prepareDataRef, prepareData]);

  return (
    <Box>
      {/* Target Section */}
      <Typography variant="subtitle1" sx={sectionHeaderSx}>
        Target
      </Typography>
      <Grid container {...gridSpacing}>
        <Grid size={{ xs: 12, md: 6 }}>
          <MemoizedTextField
            label="Name"
            value={displayValues.name}
            onChange={handlers.name}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <MemoizedTextField
            label="RA Coordinates"
            value={displayValues.raCoordinates}
            disabled={!isNightSelected}
            onChange={handlers.raCoordinates}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <MemoizedTextField
            label="DEC Coordinates"
            value={displayValues.decCoordinates}
            disabled={!isNightSelected}
            onChange={handlers.decCoordinates}
          />
        </Grid>
      </Grid>

      {/* Equipment Section */}
      <Typography variant="subtitle1" sx={sectionHeaderSx}>
        Equipment
      </Typography>
      <Grid container {...gridSpacing}>
        <Grid size={{ xs: 12, md: 6 }}>
          <MemoizedTextField
            label="Telescope"
            value={displayValues.telescopeName}
            onChange={handlers.telescopeName}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <MemoizedTextField
            label="Camera"
            value={displayValues.cameraName}
            onChange={handlers.cameraName}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MemoizedTextField
            label="Focal Length"
            value={displayValues.focalLength}
            InputProps={{ endAdornment: mmAdornment }}
            onChange={handlers.focalLength}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MemoizedTextField
            label="Focal Ratio"
            value={displayValues.focalRatio}
            onChange={handlers.focalRatio}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MemoizedTextField
            label="Pixel Size"
            value={displayValues.pixelSize}
            onChange={handlers.pixelSize}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MemoizedTextField
            label="Bit Depth"
            value={displayValues.bitDepth}
            onChange={handlers.bitDepth}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <MemoizedTextField
            label="Filters (comma separated)"
            value={displayValues.filters}
            onChange={handlers.filters}
          />
        </Grid>
      </Grid>

      {/* Location Section */}
      <Typography variant="subtitle1" sx={sectionHeaderSx}>
        Location
      </Typography>
      <Grid container {...gridSpacingLast}>
        <Grid size={{ xs: 12, md: 6 }}>
          <MemoizedTextField
            label="Location Name"
            value={displayValues.locationName}
            disabled={!isNightSelected}
            onChange={handlers.locationName}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MemoizedTextField
            label="Bortle"
            value={displayValues.bortle}
            InputProps={{ endAdornment: bortleAdornment }}
            disabled={!isNightSelected}
            onChange={handlers.bortle}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <MemoizedTextField
            label="Elevation"
            value={displayValues.elevation}
            InputProps={{ endAdornment: meterAdornment }}
            disabled={!isNightSelected}
            onChange={handlers.elevation}
          />
        </Grid>
      </Grid>
      {!isNightSelected && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info">
            Editing per-night metadata is only available when a single night is
            selected.
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default React.memo(EditForm);
